// D-SAAS-OBS-1 Phase 2 — Resources: live per-tenant DB CPU/storage/connections from Azure Monitor
// (built to the approved mockup in docs/design/D-SAAS-OBS-1/resources.png). Reads ABOUT tenant DBs,
// never queries into them. Health: green <85% peak · warning >=85% or storage>=85% · critical >=98%
// peak or connection failures. Auto-refreshes every 60s. SaaS view only.
import { useEffect, useState } from 'react'
import { Zap, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'
import { useAutoRefresh } from '../../components/useAutoRefresh'

const MUTE = 'var(--color-text-mute, #6b7280)'
const card: React.CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e7e5e4)', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }
const th: React.CSSProperties = { textAlign: 'left', fontSize: 10, color: MUTE, textTransform: 'uppercase', letterSpacing: '.03em', padding: '7px 7px', borderBottom: '1px solid var(--color-border, #e7e5e4)' }
const thR: React.CSSProperties = { ...th, textAlign: 'right' }
const td: React.CSSProperties = { padding: '8px 7px', fontSize: 12.5, borderBottom: '1px solid #f1f0ee', fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' }
const tdR: React.CSSProperties = { ...td, textAlign: 'right' }
const badge = (bg: string, fg: string): React.CSSProperties => ({ display: 'inline-block', fontSize: 10.5, fontWeight: 800, padding: '2px 9px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' })
const kpi: React.CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border,#e7e5e4)', borderRadius: 10, padding: '12px 13px' }

function Spark({ pts }: { pts: (number | null)[] }) {
  const vals = pts.map(p => (p == null ? null : Math.max(0, Math.min(100, p))))
  const w = 90, h = 22
  const n = vals.length || 1
  const path = vals.map((v, i) => (v == null ? null : `${(i / Math.max(1, n - 1)) * w},${h - (v / 100) * (h - 2) - 1}`)).filter(Boolean).join(' ')
  const peak = Math.max(...(vals.filter(v => v != null) as number[]), 0)
  const color = peak >= 98 ? '#dc2626' : peak >= 85 ? '#f59e0b' : '#22c55e'
  return <svg width={w} height={h}><polyline fill="none" stroke={color} strokeWidth={1.6} points={path} /></svg>
}
function Meter({ v }: { v: number | null }) {
  const pct = v == null ? 0 : Math.max(0, Math.min(100, v))
  const color = pct >= 98 ? '#dc2626' : pct >= 85 ? '#f59e0b' : '#22c55e'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 74, height: 9, borderRadius: 5, background: '#f1f5f9', overflow: 'hidden', border: '1px solid var(--color-border,#e7e5e4)', display: 'inline-block' }}>
        <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: color }} />
      </span>
      <b style={{ color: pct >= 98 ? '#991b1b' : pct >= 85 ? '#b45309' : undefined }}>{v == null ? '—' : `${v}%`}</b>
    </span>
  )
}

export default function ResourcesTab() {
  const [data, setData] = useState<any>(null)
  const [names, setNames] = useState<Record<string, string>>({})
  const [err, setErr] = useState<string | null>(null)
  const [ts, setTs] = useState<Date | null>(null)

  const load = async () => {
    try {
      const r = await api.opsResourceMetrics()
      setData(r.metrics); setNames(r.names_by_slug || {}); setTs(new Date()); setErr(null)
    } catch (e: any) { setErr(e?.message || 'Failed to load resource metrics') }
  }
  useEffect(() => { load() }, [])   // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(load, { intervalMs: 60000, leaderKey: 'ops.resources' })

  if (err) return <div style={{ padding: 20, color: '#991b1b', fontSize: 13 }}>{err}</div>
  if (!data) return <div style={{ padding: 20, fontSize: 13, color: MUTE }}>Loading resource metrics…</div>
  if (!data.available) return <div style={{ padding: 20, fontSize: 13, color: '#92400e' }}>Azure metrics unavailable — {data.reason}</div>

  const dbs: any[] = data.databases || []
  const online = dbs.filter(d => !d.paused)
  const paused = dbs.filter(d => d.paused)
  const crit = online.filter(d => d.health === 'critical').length
  const warn = online.filter(d => d.health === 'warning').length
  const healthy = online.filter(d => d.health === 'healthy').length
  const connFails = online.reduce((a, d) => a + (d.conn_fail_24h || 0), 0)
  const vcores = online.reduce((a, d) => a + (d.max_vcores || 0), 0)
  const dispName = (slug: string) => names[slug] || slug

  return (
    <div style={{ padding: '18px 22px', maxWidth: '100%' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
        <Zap size={18} /> Resource monitoring — per customer
      </h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: MUTE }}>
        Live from Azure Monitor (reader-only) · refreshed every 60s{ts ? <> · last {ts.toLocaleTimeString()}</> : null} ·
        thresholds: ⚠ CPU peak ≥85% · ✗ ≥98% or connection failures · CPU&gt;80% email alerts ship with the alert worker.
        <button onClick={load} style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#0f766e', background: 'none', border: 'none', cursor: 'pointer' }}>
          <RefreshCw size={11} style={{ verticalAlign: -1 }} /> refresh
        </button>
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 14 }}>
        <div style={kpi}><div style={{ fontSize: 10.5, color: MUTE, fontWeight: 700, textTransform: 'uppercase' }}>Fleet health</div>
          <div style={{ fontSize: 21, fontWeight: 800, color: crit ? '#991b1b' : warn ? '#b45309' : '#15803d' }}>
            {crit ? `${crit} critical` : warn ? `${warn} warning` : 'all healthy'}</div>
          <div style={{ fontSize: 10.5, color: MUTE }}>{warn} warning · {healthy} healthy · {paused.length} paused</div></div>
        <div style={kpi}><div style={{ fontSize: 10.5, color: MUTE, fontWeight: 700, textTransform: 'uppercase' }}>Online tenant DBs</div>
          <div style={{ fontSize: 21, fontWeight: 800 }}>{online.length}</div><div style={{ fontSize: 10.5, color: MUTE }}>{vcores} max vCores (serverless)</div></div>
        <div style={kpi}><div style={{ fontSize: 10.5, color: MUTE, fontWeight: 700, textTransform: 'uppercase' }}>Conn failures (24h)</div>
          <div style={{ fontSize: 21, fontWeight: 800, color: connFails ? '#991b1b' : '#15803d' }}>{connFails}</div><div style={{ fontSize: 10.5, color: MUTE }}>across online DBs</div></div>
        <div style={kpi}><div style={{ fontSize: 10.5, color: MUTE, fontWeight: 700, textTransform: 'uppercase' }}>Paused (idle)</div>
          <div style={{ fontSize: 21, fontWeight: 800 }}>{paused.length}</div><div style={{ fontSize: 10.5, color: MUTE }}>$0 compute while paused</div></div>
      </div>

      <div style={card}>
        <h3 style={{ fontSize: 14, margin: '0 0 2px' }}>Tenant databases</h3>
        <p style={{ fontSize: 11.5, color: MUTE, margin: '0 0 10px' }}>
          One row per customer DB. Metrics are read from Azure about the database — zero query load is added to customer databases.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Customer / database</th><th style={thR}>SKU · max vCores</th><th style={thR}>CPU now</th>
            <th style={thR}>CPU 24h</th><th style={thR}>Peak</th><th style={thR}>Storage</th>
            <th style={thR}>Sessions/Workers</th><th style={thR}>Conn ok/fail (24h)</th><th style={thR}>Health</th>
          </tr></thead>
          <tbody>
            {online.map(d => (
              <tr key={d.database} style={d.health === 'critical' ? { background: '#fef2f2' } : undefined}>
                <td style={td}><b>{dispName(d.slug)}</b><div style={{ fontFamily: 'ui-monospace,Menlo,monospace', fontSize: 11, color: MUTE }}>{d.database}</div></td>
                <td style={tdR}>{d.sku} · <b>{d.max_vcores}</b></td>
                <td style={tdR}><Meter v={d.cpu_now} /></td>
                <td style={tdR}><Spark pts={d.cpu_spark || []} /></td>
                <td style={{ ...tdR, fontWeight: 800, color: (d.cpu_peak_24h ?? 0) >= 98 ? '#991b1b' : (d.cpu_peak_24h ?? 0) >= 85 ? '#b45309' : undefined }}>
                  {d.cpu_peak_24h == null ? '—' : `${d.cpu_peak_24h}%`}</td>
                <td style={tdR}>{d.storage_pct == null ? '—' : `${d.storage_pct}%`}</td>
                <td style={tdR}>{d.sessions_pct ?? '—'}% / {d.workers_pct ?? '—'}%</td>
                <td style={tdR}>{(d.conn_ok_24h ?? 0).toLocaleString()} / <b style={{ color: d.conn_fail_24h ? '#991b1b' : '#15803d' }}>{d.conn_fail_24h ?? 0}</b></td>
                <td style={tdR}>
                  {d.health === 'critical' ? <span style={badge('#fee2e2', '#991b1b')}>CRITICAL</span>
                    : d.health === 'warning' ? <span style={badge('#fef9c3', '#854d0e')}>WARNING</span>
                    : <span style={badge('#dcfce7', '#166534')}>HEALTHY</span>}
                </td>
              </tr>
            ))}
            {paused.length > 0 && (
              <tr>
                <td style={td} colSpan={8}>{paused.map(d => dispName(d.slug)).join(', ')} <span style={{ color: MUTE, fontSize: 11 }}>({paused.length} idle)</span></td>
                <td style={tdR}><span style={badge('#f1f5f9', '#64748b')}>PAUSED — $0 compute</span></td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 16, fontSize: 11, color: MUTE, marginTop: 8 }}>
          <span>🟢 healthy: CPU peak &lt;85%, storage &lt;85%, no conn failures</span>
          <span>🟡 warning: peak ≥85% or storage ≥85%</span>
          <span>🔴 critical: peak ≥98% or connection failures</span>
        </div>
      </div>
    </div>
  )
}
